import {
  Badge,
  Button,
  Card,
  Dialog,
  Drawer,
  Input,
  Label,
  PageHeader,
  Select,
  Tabs,
  buttonVariants,
} from "@safeplate/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Archive,
  ChevronDown,
  ChevronRight,
  ClipboardPlus,
  ExternalLink,
  Filter,
  ImageIcon,
  Paperclip,
  Pencil,
  X,
} from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";

import { ApiError, displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import {
  dateTimeLabel,
  timeLabel,
  instantFromLocalInput,
  isDateOnly,
  localInputValue,
  zonedDayEnd,
  zonedDayStart,
} from "../dashboard/local-day.js";
import { typedLogForms } from "../logging/catalog.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import { dayHeading } from "../tasks/tasks-view.js";
import { todayIn } from "../reports/report-period.js";
import {
  editRecord,
  getRecord,
  getAttachmentDownload,
  getRecordRevisions,
  getRecordTypes,
  humanLabel,
  listRecords,
  loadRecordNames,
  resultTone,
  voidRecord,
  type RecordEntry,
  type RecordFilters,
  type RecordOutcome,
} from "./records-api.js";
import {
  changeLines,
  detailRows,
  groupRecordsByDay,
  recordFieldLabel,
  recordHeadline,
  recordResultBadge,
  shortTypeName,
  type DetailRow,
  type RecordNames,
} from "./records-view.js";

type ResultFilter = RecordOutcome | "outside";

const resultFilters: readonly ResultFilter[] = [
  "pass",
  "warn",
  "fail",
  "na",
  "outside",
];

function resultLabel(outcome: RecordEntry["overall_result"]): string {
  if (outcome === "pass") return "Pass";
  if (outcome === "warn") return "Warning";
  if (outcome === "fail") return "Fail";
  if (outcome === "na") return "Not applicable";
  return "Not checked";
}

function RecordAttachment({
  attachment,
}: {
  attachment: RecordEntry["attachments"][number];
}) {
  const download = useQuery({
    queryFn: () => getAttachmentDownload(attachment.attachment_id),
    queryKey: ["attachment-download", attachment.attachment_id, "display"],
    refetchInterval: (query) => {
      if (query.state.data) return false;
      const error = query.state.error;
      return !error || (error instanceof ApiError && error.status === 409)
        ? 5_000
        : false;
    },
    retry: false,
  });

  return (
    <figure className="overflow-hidden rounded-2xl border border-stone-200 bg-surface">
      {download.data ? (
        <img
          alt={attachment.caption ?? "Evidence photo"}
          className="max-h-[32rem] w-full bg-stone-100 object-contain"
          src={download.data.download_url}
        />
      ) : (
        <div className="grid min-h-48 place-items-center bg-stone-100 px-5 text-center text-stone-500">
          <div>
            <ImageIcon className="mx-auto size-7" />
            <p className="mt-2 text-sm font-semibold">
              {download.isFetching
                ? "Preparing photo…"
                : "Photo is still processing"}
            </p>
            {!download.isFetching ? (
              <Button
                className="mt-2"
                onClick={() => void download.refetch()}
                size="sm"
                variant="ghost"
              >
                Try again
              </Button>
            ) : null}
          </div>
        </div>
      )}
      <figcaption className="flex items-center gap-3 px-4 py-3">
        <Paperclip className="size-4 shrink-0 text-stone-400" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">
          {attachment.caption ?? "Evidence photo"}
        </span>
        {download.data ? (
          <a
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800"
            href={download.data.download_url}
            rel="noreferrer"
            target="_blank"
          >
            Full size <ExternalLink className="size-3" />
          </a>
        ) : null}
      </figcaption>
    </figure>
  );
}

function editableKind(
  key: string,
  value: unknown,
): "boolean" | "json" | "number" | "text" {
  if (
    typeof value === "boolean" ||
    key.startsWith("is_") ||
    key.startsWith("expects_") ||
    key.startsWith("served_")
  )
    return "boolean";
  if (typeof value === "number" || /(_c|_g|_aw|_minutes|_count)$/.test(key))
    return "number";
  if (typeof value === "object" && value !== null) return "json";
  return "text";
}

function parseDetailValue(
  kind: ReturnType<typeof editableKind>,
  raw: string,
): unknown {
  if (raw === "") return null;
  if (kind === "boolean") return raw === "true";
  if (kind === "number") return Number(raw);
  if (kind === "json") return JSON.parse(raw) as unknown;
  return raw;
}

function useRecordTimezone(locationId: string): string {
  const shell = useShell();
  return (
    shell.locations.find((entry) => entry.id === locationId)?.timezone ??
    shell.activeLocation?.timezone ??
    "Pacific/Auckland"
  );
}

const hiddenEditKeys = new Set(["id", "target_min_c", "target_max_c"]);

// Ids are never edited by hand, so they are neither shown nor sent back.
function editableEntries(
  source: Record<string, unknown>,
): Array<[string, unknown]> {
  return Object.entries(source).filter(
    ([key]) => !hiddenEditKeys.has(key) && !key.endsWith("_id"),
  );
}

function RecordEditor({
  onClose,
  onSaved,
  record,
}: {
  onClose: () => void;
  onSaved: (record: RecordEntry) => void;
  record: RecordEntry;
}) {
  const id = useId();
  const timezone = useRecordTimezone(record.location_id);
  const staff = useQuery({
    queryFn: () => listStaff(record.location_id),
    queryKey: ["staff", record.location_id],
  });
  const source = record.detail ?? record.payload ?? {};
  const storageKey = record.detail ? "detail" : "payload";
  const code = record.record_type_code;
  const choices = new Map(
    (typedLogForms[code]?.fields ?? [])
      .filter((field) => field.kind === "select")
      .map((field) => [field.key, field.options ?? []] as const),
  );
  const formId = `${id}-form`;
  const edit = useMutation({
    mutationFn: (form: FormData) => {
      const changed: Record<string, unknown> = {};
      for (const [key, original] of editableEntries(source)) {
        const raw = String(form.get(`value.${key}`) ?? "");
        const next = parseDetailValue(editableKind(key, original), raw);
        if (JSON.stringify(next) !== JSON.stringify(original))
          changed[key] = next;
      }
      const nextNote = String(form.get("note") ?? "").trim() || null;
      const nextPerformer = String(form.get("performed_by_staff_id") ?? "");
      const nextOccurred = instantFromLocalInput(
        String(form.get("occurred_at")),
        timezone,
      ).toISOString();
      const input = {
        based_on_revision: record.current_revision,
        ...(Object.keys(changed).length > 0 ? { [storageKey]: changed } : {}),
        ...(nextNote !== record.note ? { note: nextNote } : {}),
        ...(nextPerformer !== record.performed_by_staff_id
          ? { performed_by_staff_id: nextPerformer }
          : {}),
        ...(nextOccurred !== record.occurred_at
          ? { occurred_at: nextOccurred }
          : {}),
        reason: String(form.get("reason") ?? "").trim(),
      };
      return editRecord(record.id, input);
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={edit.isPending} form={formId} type="submit">
            {edit.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="lg"
      title={`Edit ${shortTypeName(code).toLowerCase()}`}
    >
      <form
        id={formId}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          edit.mutate(new FormData(event.currentTarget));
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor={`${id}-occurred`}>When</Label>
            <Input
              className="mt-1.5"
              defaultValue={localInputValue(record.occurred_at, timezone)}
              id={`${id}-occurred`}
              name="occurred_at"
              required
              type="datetime-local"
            />
          </div>
          <div>
            <Label htmlFor={`${id}-performer`}>Performed by</Label>
            <Select
              defaultValue={record.performed_by_staff_id}
              id={`${id}-performer`}
              name="performed_by_staff_id"
              wrapperClassName="mt-1.5"
            >
              <option value={record.performed_by_staff_id}>
                {staff.data?.find(
                  (person) => person.id === record.performed_by_staff_id,
                )?.display_name ?? "Current performer"}
              </option>
              {staff.data
                ?.filter((person) => person.id !== record.performed_by_staff_id)
                .map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.display_name}
                  </option>
                ))}
            </Select>
          </div>
          {editableEntries(source).map(([key, value]) => {
            const kind = editableKind(key, value);
            const label = recordFieldLabel(code, key);
            const options = choices.get(key);
            const shown =
              kind === "json"
                ? JSON.stringify(value, null, 2)
                : value === null || value === undefined
                  ? ""
                  : String(value);
            const wide = kind === "json" || String(value).length > 80;
            return (
              <div className={wide ? "sm:col-span-2" : ""} key={key}>
                <Label htmlFor={`${id}-${key}`}>{label}</Label>
                {kind === "boolean" ? (
                  <Select
                    defaultValue={String(value ?? false)}
                    id={`${id}-${key}`}
                    name={`value.${key}`}
                    wrapperClassName="mt-1.5"
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Select>
                ) : options && options.length > 0 ? (
                  <Select
                    defaultValue={shown}
                    id={`${id}-${key}`}
                    name={`value.${key}`}
                    wrapperClassName="mt-1.5"
                  >
                    {options.some((entry) => entry.value === shown) ? null : (
                      <option value={shown}>{humanLabel(shown)}</option>
                    )}
                    {options.map((entry) => (
                      <option key={entry.value} value={entry.value}>
                        {entry.label}
                      </option>
                    ))}
                  </Select>
                ) : wide ? (
                  <textarea
                    className="mt-1.5 min-h-24 w-full rounded-xl border border-stone-300 bg-surface px-3.5 py-3 text-sm"
                    defaultValue={shown}
                    id={`${id}-${key}`}
                    name={`value.${key}`}
                  />
                ) : (
                  <Input
                    className="mt-1.5"
                    defaultValue={shown}
                    id={`${id}-${key}`}
                    name={`value.${key}`}
                    step={kind === "number" ? "any" : undefined}
                    type={kind === "number" ? "number" : "text"}
                  />
                )}
              </div>
            );
          })}
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-note`}>Note</Label>
            <textarea
              className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 bg-surface px-3.5 py-3 text-sm"
              defaultValue={record.note ?? ""}
              id={`${id}-note`}
              name="note"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-reason`}>Reason</Label>
            <Input
              className="mt-1.5"
              id={`${id}-reason`}
              name="reason"
              required
            />
          </div>
        </div>
        {edit.error ? (
          <div
            className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
            role="alert"
          >
            {edit.error instanceof ApiError &&
            edit.error.code === "revision_conflict"
              ? "Someone else changed this record. Close this, review it, then try again."
              : displayError(edit.error)}
          </div>
        ) : null}
      </form>
    </Dialog>
  );
}

function DetailList({ rows }: { rows: readonly DetailRow[] }) {
  return (
    <dl className="divide-y divide-stone-200 border-y border-stone-200">
      {rows.map((row) => (
        <div
          className="grid gap-1 py-3 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)] sm:gap-4"
          key={row.key}
        >
          <dt className="text-sm text-stone-500">{row.label}</dt>
          <dd className="min-w-0 text-sm font-semibold break-words whitespace-pre-wrap text-stone-950">
            {row.items ? (
              <ul className="divide-y divide-stone-100">
                {row.items.map((item, index) => (
                  <li className="py-2 first:pt-0 last:pb-0" key={index}>
                    {item.title || item.badge ? (
                      <p className="flex flex-wrap items-center gap-2">
                        {item.title ? <span>{item.title}</span> : null}
                        {item.badge ? (
                          <Badge tone={item.badge.tone}>
                            {item.badge.label}
                          </Badge>
                        ) : null}
                      </p>
                    ) : null}
                    {item.fields.map((field) => (
                      <p
                        className="mt-0.5 flex justify-between gap-3 text-xs font-normal text-stone-500"
                        key={field.label}
                      >
                        <span>{field.label}</span>
                        <span className="text-right font-semibold text-stone-800">
                          {field.text}
                        </span>
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            ) : (
              row.text
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function useRecordNames(locationId: string | undefined, canManage: boolean) {
  const shell = useShell();
  const names = useQuery({
    enabled: Boolean(locationId),
    queryFn: () => loadRecordNames(locationId!, canManage),
    queryKey: ["record-names", locationId, canManage],
    staleTime: 60_000,
  });
  return useMemo<RecordNames>(() => {
    const merged = new Map(names.data ?? []);
    // People without the team list still see themselves by name.
    for (const id of shell.ownStaffProfileIds)
      if (!merged.has(id)) merged.set(id, shell.userName || "You");
    return merged;
  }, [names.data, shell.ownStaffProfileIds, shell.userName]);
}

export function RecordDrawer({
  canManage,
  onClose,
  recordId,
}: {
  canManage: boolean;
  onClose: () => void;
  recordId: string;
}) {
  const queryClient = useQueryClient();
  const shell = useShell();
  const [tab, setTab] = useState<"changes" | "details">("details");
  const [editing, setEditing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const record = useQuery({
    queryFn: () => getRecord(recordId),
    queryKey: ["record", recordId],
  });
  const timezone = useRecordTimezone(
    record.data?.location_id ?? shell.activeLocation?.id ?? "",
  );
  const types = useQuery({
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const names = useRecordNames(record.data?.location_id, canManage);
  const performer = record.data
    ? names.get(record.data.performed_by_staff_id)
    : undefined;
  const revisions = useQuery({
    enabled: canManage && tab === "changes",
    queryFn: () => getRecordRevisions(recordId),
    queryKey: ["record-revisions", recordId],
  });
  const voidMutation = useMutation({
    mutationFn: (reason: string) => voidRecord(recordId, reason),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["record", recordId], updated);
      await queryClient.invalidateQueries({ queryKey: ["records"] });
      setVoiding(false);
    },
  });
  const data = record.data;
  const typeTitle = data
    ? types.data?.find((type) => type.code === data.record_type_code)?.title
    : undefined;
  const rows = data ? detailRows(data, names, timezone) : [];

  return (
    <Drawer label="Record details" onClose={onClose}>
      <div>
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-stone-200 bg-surface px-5 py-4 sm:px-7">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold break-words">
              {data ? recordHeadline(data, names, typeTitle) : "Loading"}
            </h2>
            {data ? (
              <p className="mt-1 text-sm text-stone-500">
                {shortTypeName(data.record_type_code, typeTitle)}
              </p>
            ) : null}
          </div>
          <Button
            aria-label="Close"
            autoFocus
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </header>
        {record.isPending ? (
          <div className="p-7">
            <LoadingState label="Loading" />
          </div>
        ) : null}
        {record.error ? (
          <div className="p-7">
            <ErrorState
              message={displayError(record.error)}
              onAction={() => void record.refetch()}
              title="This record isn’t available"
            />
          </div>
        ) : null}
        {data ? (
          <>
            {canManage && data.current_revision > 0 ? (
              <div className="border-b border-stone-200 px-5 py-3 sm:px-7">
                <Tabs
                  ariaLabel="Record"
                  items={[
                    { label: "Details", value: "details" },
                    {
                      label: `Changes (${data.current_revision})`,
                      value: "changes",
                    },
                  ]}
                  onChange={(value) =>
                    setTab(value === "changes" ? "changes" : "details")
                  }
                  value={tab}
                  variant="segmented"
                />
              </div>
            ) : null}
            <div className="space-y-6 p-5 sm:p-7">
              {tab === "details" || !canManage ? (
                <>
                  {data.status === "voided" ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                      <p className="font-semibold">Voided</p>
                      {data.voided_reason ? (
                        <p className="mt-1">{data.voided_reason}</p>
                      ) : null}
                    </div>
                  ) : null}
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
                    <div>
                      <dt className="text-sm text-stone-500">When</dt>
                      <dd className="mt-1 text-sm font-semibold">
                        {dateTimeLabel(data.occurred_at, timezone)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-stone-500">Result</dt>
                      <dd className="mt-1">
                        <Badge tone={resultTone(data.overall_result)}>
                          {resultLabel(data.overall_result)}
                        </Badge>
                      </dd>
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <dt className="text-sm text-stone-500">Performed by</dt>
                      <dd className="mt-1 text-sm font-semibold break-words">
                        {performer ?? "Unknown"}
                      </dd>
                    </div>
                  </dl>
                  {rows.length > 0 ? <DetailList rows={rows} /> : null}
                  {data.note ? (
                    <section>
                      <h3 className="mb-2 text-sm font-semibold">Note</h3>
                      <p className="text-sm leading-6 whitespace-pre-wrap text-stone-700">
                        {data.note}
                      </p>
                    </section>
                  ) : null}
                  {data.attachments.length > 0 ? (
                    <section>
                      <h3 className="mb-3 text-sm font-semibold">Photos</h3>
                      <div className="space-y-2">
                        {data.attachments.map((attachment) => (
                          <RecordAttachment
                            attachment={attachment}
                            key={attachment.attachment_id}
                          />
                        ))}
                      </div>
                    </section>
                  ) : null}
                  {canManage && data.status === "active" ? (
                    <div className="flex flex-wrap justify-end gap-2 border-t border-stone-200 pt-5">
                      <Button onClick={() => setVoiding(true)} variant="ghost">
                        <Archive className="size-4" /> Void
                      </Button>
                      <Button onClick={() => setEditing(true)}>
                        <Pencil className="size-4" /> Edit
                      </Button>
                    </div>
                  ) : null}
                </>
              ) : (
                <section>
                  {revisions.isPending ? (
                    <LoadingState label="Loading" />
                  ) : null}
                  {revisions.error ? (
                    <ErrorState
                      message={displayError(revisions.error)}
                      title="Changes aren’t available"
                    />
                  ) : null}
                  {revisions.data?.length === 0 ? (
                    <EmptyState calm title="No changes" />
                  ) : null}
                  <ul className="divide-y divide-stone-200 border-y border-stone-200">
                    {revisions.data?.map((revision) => (
                      <li className="py-4" key={revision.id}>
                        <p className="text-sm font-semibold">
                          {dateTimeLabel(revision.changed_at, timezone)}
                        </p>
                        {revision.reason ? (
                          <p className="mt-1 text-sm text-stone-700">
                            {revision.reason}
                          </p>
                        ) : null}
                        <dl className="mt-3 space-y-1.5 text-sm">
                          {changeLines(
                            revision,
                            data.record_type_code,
                            names,
                            timezone,
                          ).map((line) => (
                            <div
                              className="flex flex-wrap gap-x-3"
                              key={line.label}
                            >
                              <dt className="text-stone-500">{line.label}</dt>
                              <dd className="font-semibold break-words">
                                {line.before} to {line.after}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </>
        ) : null}
      </div>
      {editing && data ? (
        <RecordEditor
          onClose={() => setEditing(false)}
          onSaved={async (updated) => {
            queryClient.setQueryData(["record", recordId], updated);
            await queryClient.invalidateQueries({ queryKey: ["records"] });
            setEditing(false);
          }}
          record={data}
        />
      ) : null}
      <Dialog
        footer={
          <>
            <Button onClick={() => setVoiding(false)} variant="ghost">
              Cancel
            </Button>
            <Button
              disabled={voidMutation.isPending}
              form="void-form"
              type="submit"
              variant="destructive"
            >
              {voidMutation.isPending ? "Voiding…" : "Void"}
            </Button>
          </>
        }
        onClose={() => setVoiding(false)}
        open={voiding}
        size="md"
        title="Void this record?"
      >
        <form
          id="void-form"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            voidMutation.mutate(
              String(new FormData(event.currentTarget).get("reason") ?? ""),
            );
          }}
        >
          <Label htmlFor="void-reason">Reason</Label>
          <textarea
            className="mt-1.5 min-h-24 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
            id="void-reason"
            name="reason"
            required
          />
          {voidMutation.error ? (
            <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
              {displayError(voidMutation.error)}
            </p>
          ) : null}
        </form>
      </Dialog>
    </Drawer>
  );
}

interface RecordsPage {
  items: RecordEntry[];
  next_cursor: string | null;
  outside_next?: { fail: string | null; warn: string | null } | undefined;
}

type OutsideCursor = { fail: string | null; warn: string | null };

// The API filters on one result at a time, so "outside the usual range" reads
// warnings and failures together and merges them newest first.
async function fetchRecords(
  locationId: string,
  filters: RecordFilters,
  outside: boolean,
  pageParam: OutsideCursor | string | undefined,
): Promise<RecordsPage> {
  if (!outside) {
    return listRecords(
      locationId,
      filters,
      typeof pageParam === "string" ? pageParam : undefined,
    );
  }
  const cursors: OutsideCursor =
    pageParam && typeof pageParam === "object"
      ? pageParam
      : { fail: null, warn: null };
  const first = pageParam === undefined;
  const [warnings, failures] = await Promise.all(
    (["warn", "fail"] as const).map((kind) =>
      first || cursors[kind]
        ? listRecords(
            locationId,
            { ...filters, outcome: kind },
            cursors[kind] ?? undefined,
          )
        : Promise.resolve({ items: [], next_cursor: null }),
    ),
  );
  const next = {
    fail: failures?.next_cursor ?? null,
    warn: warnings?.next_cursor ?? null,
  };
  return {
    items: [...(warnings?.items ?? []), ...(failures?.items ?? [])].sort(
      (a, b) =>
        new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
    ),
    next_cursor: null,
    outside_next: next.fail || next.warn ? next : undefined,
  };
}

type ParamKey = "by" | "from" | "outcome" | "record" | "to" | "type";

export function RecordsWorkspacePage() {
  const shell = useShell();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const locationId = shell.activeLocation?.id;
  const timezone = shell.activeLocation?.timezone ?? "Pacific/Auckland";
  const canManage = shell.activeRole !== "staff";
  const text = (key: string): string | undefined =>
    typeof search[key] === "string" && search[key] !== ""
      ? (search[key] as string)
      : undefined;
  const outcome = resultFilters.find((value) => value === text("outcome"));
  const typeParam = text("type");
  const performerParam = text("by");
  const fromParam = isDateOnly(text("from")) ? text("from") : undefined;
  const toParam = isDateOnly(text("to")) ? text("to") : undefined;
  const includeVoided = [true, 1, "1", "true"].includes(
    search.voided as boolean,
  );
  const selectedId = text("record") ?? null;

  function setParams(
    patch: Partial<Record<ParamKey, string | undefined>> & {
      voided?: true | undefined;
    },
    replace = true,
  ) {
    const next: Record<string, boolean | string> = {
      ...(outcome ? { outcome } : {}),
      ...(typeParam ? { type: typeParam } : {}),
      ...(performerParam ? { by: performerParam } : {}),
      ...(fromParam ? { from: fromParam } : {}),
      ...(toParam ? { to: toParam } : {}),
      ...(includeVoided ? { voided: true } : {}),
      ...(selectedId ? { record: selectedId } : {}),
    };
    for (const [key, value] of Object.entries(patch)) {
      if (value) next[key] = value;
      else delete next[key];
    }
    void navigate({ replace, search: next, to: "/records" });
  }

  const filters: RecordFilters = {
    includeVoided,
    ...(typeParam ? { recordType: typeParam } : {}),
    ...(outcome && outcome !== "outside" ? { outcome } : {}),
    ...(performerParam ? { performer: performerParam } : {}),
    ...(fromParam
      ? { occurredFrom: zonedDayStart(fromParam, timezone).toISOString() }
      : {}),
    ...(toParam
      ? { occurredTo: zonedDayEnd(toParam, timezone).toISOString() }
      : {}),
  };
  const filtered =
    includeVoided ||
    Boolean(outcome || typeParam || performerParam || fromParam || toParam);
  const types = useQuery({
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });
  const records = useInfiniteQuery({
    enabled: Boolean(locationId),
    getNextPageParam: (page: RecordsPage) =>
      page.outside_next ?? page.next_cursor ?? undefined,
    initialPageParam: undefined as OutsideCursor | string | undefined,
    queryFn: ({ pageParam }) =>
      fetchRecords(locationId!, filters, outcome === "outside", pageParam),
    queryKey: ["records", locationId, filters, outcome === "outside"],
  });
  const items = useMemo(
    () => records.data?.pages.flatMap((page: RecordsPage) => page.items) ?? [],
    [records.data],
  );
  const names = useRecordNames(locationId, canManage);
  const days = useMemo(
    () => groupRecordsByDay(items, timezone),
    [items, timezone],
  );
  const today = todayIn(timezone);

  return (
    <>
      <PageHeader
        actions={
          <Link className={buttonVariants()} to="/log">
            <ClipboardPlus className="size-4" /> New log
          </Link>
        }
        className="mb-6"
        title={canManage ? "Records" : "History"}
      />
      {!locationId ? (
        <EmptyState title="Choose a location" />
      ) : (
        <div className="space-y-4">
          <details
            className="group rounded-xl border border-stone-200 bg-surface"
            open={filtered}
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-4">
              <Filter className="size-4 text-stone-400" />
              <span className="text-sm font-black">Filters</span>
              {filtered ? <Badge tone="neutral">On</Badge> : null}
              <ChevronDown className="ml-auto size-4 text-stone-400 transition group-open:rotate-180" />
            </summary>
            <div className="grid gap-4 border-t border-stone-200 px-4 py-5 sm:grid-cols-2 xl:grid-cols-5">
              <div>
                <Label htmlFor="record-type">Record type</Label>
                <Select
                  id="record-type"
                  onChange={(event) =>
                    setParams({ type: event.target.value || undefined })
                  }
                  value={typeParam ?? ""}
                  wrapperClassName="mt-1.5"
                >
                  <option value="">All</option>
                  {types.data?.map((type) => (
                    <option key={type.code} value={type.code}>
                      {type.title}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="record-result">Result</Label>
                <Select
                  id="record-result"
                  onChange={(event) =>
                    setParams({ outcome: event.target.value || undefined })
                  }
                  value={outcome ?? ""}
                  wrapperClassName="mt-1.5"
                >
                  <option value="">Any</option>
                  <option value="outside">Outside the usual range</option>
                  <option value="pass">Pass</option>
                  <option value="warn">Warning</option>
                  <option value="fail">Fail</option>
                  <option value="na">Not applicable</option>
                </Select>
              </div>
              {canManage && staff.data?.length ? (
                <div>
                  <Label htmlFor="record-person">Performed by</Label>
                  <Select
                    id="record-person"
                    onChange={(event) =>
                      setParams({ by: event.target.value || undefined })
                    }
                    value={performerParam ?? ""}
                    wrapperClassName="mt-1.5"
                  >
                    <option value="">Anyone</option>
                    {staff.data.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.display_name}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2 xl:col-span-2">
                <div>
                  <Label htmlFor="record-from">From</Label>
                  <Input
                    className="mt-1.5 px-2 text-xs"
                    id="record-from"
                    onChange={(event) =>
                      setParams({ from: event.target.value || undefined })
                    }
                    type="date"
                    value={fromParam ?? ""}
                  />
                </div>
                <div>
                  <Label htmlFor="record-to">To</Label>
                  <Input
                    className="mt-1.5 px-2 text-xs"
                    id="record-to"
                    onChange={(event) =>
                      setParams({ to: event.target.value || undefined })
                    }
                    type="date"
                    value={toParam ?? ""}
                  />
                </div>
              </div>
              <label className="flex cursor-pointer items-end gap-3 py-3 text-sm font-semibold sm:col-span-2 xl:col-span-5">
                <input
                  checked={includeVoided}
                  className="size-4 accent-emerald-800"
                  onChange={(event) =>
                    setParams({
                      voided: event.target.checked ? true : undefined,
                    })
                  }
                  type="checkbox"
                />{" "}
                Include voided
              </label>
            </div>
          </details>
          <div className="min-w-0">
            {records.isPending ? <LoadingState label="Loading" /> : null}
            {records.error ? (
              <ErrorState
                message={displayError(records.error)}
                onAction={() => void records.refetch()}
                title="Records aren’t available"
              />
            ) : null}
            {!records.isPending && !records.error && items.length === 0 ? (
              <EmptyState
                title={filtered ? "No matching records" : "No records"}
              />
            ) : null}
            {items.length > 0 ? (
              <Card className="overflow-hidden">
                {days.map((day) => (
                  <section key={day.date}>
                    <h2 className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-xs font-semibold tracking-widest text-stone-500 uppercase sm:px-5">
                      {dayHeading(day.date, today)}
                    </h2>
                    <div className="divide-y divide-stone-100">
                      {day.items.map((record) => {
                        const badge = recordResultBadge(record);
                        const performer = names.get(
                          record.performed_by_staff_id,
                        );
                        return (
                          <button
                            className="flex min-h-14 w-full items-center gap-3 px-4 py-3 focus-inset text-left transition hover:bg-stone-50 sm:px-5"
                            key={record.id}
                            onClick={() =>
                              setParams({ record: record.id }, false)
                            }
                            type="button"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold break-words">
                                {recordHeadline(
                                  record,
                                  names,
                                  types.data?.find(
                                    (type) =>
                                      type.code === record.record_type_code,
                                  )?.title,
                                )}
                              </p>
                              <p className="mt-0.5 text-xs text-stone-500">
                                {[
                                  timeLabel(record.occurred_at, timezone),
                                  performer,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                            </div>
                            {badge ? (
                              <Badge tone={badge.tone}>{badge.label}</Badge>
                            ) : null}
                            <ChevronRight className="size-4 shrink-0 text-stone-300" />
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </Card>
            ) : null}
            {records.hasNextPage ? (
              <div className="mt-4 text-center">
                <Button
                  disabled={records.isFetchingNextPage}
                  onClick={() => void records.fetchNextPage()}
                  variant="outline"
                >
                  {records.isFetchingNextPage ? "Loading…" : "Load older"}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
      {selectedId ? (
        <RecordDrawer
          canManage={canManage}
          onClose={() => setParams({ record: undefined })}
          recordId={selectedId}
        />
      ) : null}
    </>
  );
}
